import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useShoppingList } from '../context/ShoppingListContext';
import { useFridgeScan } from '../context/FridgeScanContext';
import api from '../services/api';
import CameraCapture from '../components/CameraCapture';
import { FRIDGE, PUSH } from '../constants/strings';
import { isPushEnabled, registerPush } from '../services/pushService';
import { formatTime } from '../utils/time';
import {
  IconNavCamera, IconPhoto, IconNavPlus, IconNavShopping, IconClipboardCopy, IconClipboardDone,
  IconCheck, IconClose, IconChevUp, IconChevDown, IconNavSearch,
  IconFavorite, IconError, IconUsers, IconClock, IconChefHat, IconBrandWhatsapp, IconShareAction, IconWand,
} from '../components/icons';
import './FridgeScan.css';
import './demo/DemoFeatures.css';

const SCAN_STEPS = [
  FRIDGE.scanStep1,
  FRIDGE.scanStep2,
  FRIDGE.scanStep3,
  FRIDGE.scanStep4,
];

function _getPlaceholderColor(recipe) {
  const cat = recipe.category || '';
  if (cat.includes('Dessert') || cat.includes('Süß')) return 'amber';
  if (cat.includes('Vegetar') || cat.includes('Vegan') || cat.includes('Salat')) return 'green';
  if (cat.includes('Suppe') || cat.includes('Fisch')) return 'blue';
  if (cat.includes('Fleisch') || cat.includes('Grill')) return 'red';
  const charCode = (recipe.title || 'A').charCodeAt(0);
  const colors = ['green', 'amber', 'blue', 'red', 'purple'];
  return colors[charCode % colors.length];
}

function ConfidenceChip({ name, confidence }) {
  const level = confidence >= 0.8 ? 'high' : confidence >= 0.5 ? 'mid' : 'low';
  return (
    <span className={`fridge-ing-chip fridge-ing-chip--${level}`}>
      {name}
      <span className="fridge-ing-conf">{Math.round(confidence * 100)}%</span>
    </span>
  );
}

function DbRecipeCard({ recipe }) {
  const score = recipe.match_score || 0;
  const missing = recipe.missing_ingredients || [];
  const [showMissing, setShowMissing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addedAll, setAddedAll] = useState(false);
  const [addedSingle, setAddedSingle] = useState({});
  const { addItems } = useShoppingList();

  const text = `Einkaufsliste für „${recipe.title}" (Strixly):\n${missing.map(i => `• ${i}`).join('\n')}`;
  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  function handleShare() { navigator.share({ text }).catch(() => {}); }
  function handleWhatsApp() { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'); }
  async function handleCopy() {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  }

  function handleAddAll() {
    addItems(missing.map(name => ({ name, amount: '', recipeId: recipe.id })));
    setAddedAll(true);
    setAddedSingle(Object.fromEntries(missing.map(name => [name, true])));
    setTimeout(() => setAddedAll(false), 2000);
  }

  function handleToggleSingle(name) {
    const alreadyAdded = addedSingle[name];
    if (!alreadyAdded) {
      addItems([{ name, amount: '', recipeId: recipe.id }]);
      setAddedSingle(prev => ({ ...prev, [name]: true }));
    } else {
      setAddedSingle(prev => ({ ...prev, [name]: false }));
    }
  }

  return (
    <div className="fridge-db-card-wrap">
      <Link to={`/recipe/${recipe.id}`} className="fridge-db-card">
        <div className="fridge-db-card-img">
          {recipe.image_path
            ? <img src={recipe.image_path} alt={recipe.title} />
            : (
              <div className={`fridge-db-placeholder fridge-db-placeholder--${_getPlaceholderColor(recipe)}`}>
                <span className="fridge-db-placeholder-initial">{(recipe.title || '?')[0].toUpperCase()}</span>
              </div>
            )}
        </div>
        <div className="fridge-db-card-body">
          <div className="fridge-db-card-title">{recipe.title}</div>
          {recipe.category && <div className="fridge-db-card-cat">{recipe.category}</div>}
          <div className="fridge-db-card-match">
            <div className="fridge-match-bar">
              <div className="fridge-match-fill" style={{ width: `${score}%` }} />
            </div>
            <span>{recipe.match_count} von {recipe.total_ingredients} Zutaten</span>
          </div>
        </div>
      </Link>
      {missing.length > 0 && (
        <div className="fridge-db-missing">
          <button className="fridge-db-missing-toggle" onClick={() => setShowMissing(s => !s)}>
            {showMissing ? <IconChevUp size={12} /> : <IconChevDown size={12} />} {missing.length} fehlende Zutat{missing.length !== 1 ? 'en' : ''} — Einkaufszettel
          </button>
          {showMissing && (
            <>
              <div className="fridge-db-missing-list">
                {missing.map((ing, i) => {
                  const added = !!addedSingle[ing];
                  return (
                    <span
                      key={i}
                      className={`fridge-db-missing-item${added ? ' fridge-db-missing-item--added' : ''}`}
                      onClick={() => handleToggleSingle(ing)}
                      title={added ? 'Aus Einkaufsliste entfernen' : 'Zur Einkaufsliste hinzufügen'}
                    >
                      {added ? <IconCheck size={12} /> : '+'} {ing}
                    </span>
                  );
                })}
              </div>
              <div className="fridge-shopping-actions">
                <button className="fridge-btn-action fridge-btn-cart" onClick={handleAddAll}>
                  {addedAll ? <><IconCheck size={14} /> Hinzugefügt</> : <><IconNavShopping size={14} /> Alle zur Einkaufsliste</>}
                </button>
                {hasNativeShare && (
                  <button className="fridge-btn-action fridge-btn-share" onClick={handleShare}><IconShareAction size={14} /> Teilen</button>
                )}
                <button className="fridge-btn-action fridge-btn-whatsapp" onClick={handleWhatsApp}>
                  <IconBrandWhatsapp size={15} style={{ flexShrink: 0 }} />
                  WhatsApp
                </button>
                <button className="fridge-btn-action fridge-btn-copy" onClick={handleCopy}>
                  {copied ? <><IconClipboardDone size={14} /> Kopiert!</> : <><IconClipboardCopy size={14} /> Kopieren</>}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const REASON_TAGS = [
  { key: 'too_complex',   label: 'Zu aufwendig' },
  { key: 'ingredients',   label: 'Mag ich diese Zutaten nicht' },
  { key: 'wrong_cuisine', label: 'Falsche Küche' },
  { key: 'too_simple',    label: 'Zu simpel' },
];

function AiRecipeCard({ recipe, onSave, saving }) {
  const [expanded, setExpanded] = useState(false);
  const [feedback, setFeedback] = useState(null); // null | 'liked' | 'disliked'
  const [showDislikePopup, setShowDislikePopup] = useState(false);
  const [reasonTag, setReasonTag] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleLike() {
    if (feedback) return;
    setFeedback('liked');
    try {
      await api.saveFridgeFeedback({
        recipe_title: recipe.title,
        recipe_category: recipe.category || '',
        liked: true,
      });
    } catch {}
  }

  function handleDislikeOpen() {
    if (feedback) return;
    setShowDislikePopup(true);
  }

  async function handleDislikeSubmit() {
    if (!reasonTag) return;
    setSubmitting(true);
    try {
      await api.saveFridgeFeedback({
        recipe_title: recipe.title,
        recipe_category: recipe.category || '',
        liked: false,
        reason_tag: reasonTag,
        note: note.trim() || undefined,
      });
      setFeedback('disliked');
      setShowDislikePopup(false);
    } catch {} finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fridge-ai-card">
      <div className="fridge-ai-card-header">
        <div className={`fridge-ai-placeholder fridge-ai-placeholder--${_getPlaceholderColor(recipe)}`}>
          <span className="fridge-ai-placeholder-initial">{(recipe.title || '?')[0].toUpperCase()}</span>
        </div>
        <div className="fridge-ai-badge"><IconWand size={12} /> {FRIDGE.aiBadge}</div>
        <h3 className="fridge-ai-title">{recipe.title}</h3>
        {recipe.description && <p className="fridge-ai-desc">{recipe.description}</p>}
        <div className="fridge-ai-meta">
          {recipe.portions && <span><IconUsers size={13} /> {recipe.portions} Portionen</span>}
          {recipe.prep_time > 0 && <span><IconClock size={13} /> {formatTime(recipe.prep_time)}</span>}
          {recipe.cook_time > 0 && <span><IconChefHat size={13} /> {formatTime(recipe.cook_time)}</span>}
        </div>
      </div>

      <button className="fridge-ai-toggle" onClick={() => setExpanded(e => !e)}>
        {expanded ? <><IconChevUp size={13} /> Weniger anzeigen</> : <><IconChevDown size={13} /> Rezept anzeigen</>}
      </button>

      {expanded && (
        <div className="fridge-ai-body">
          {recipe.ingredients?.length > 0 && (
            <div className="fridge-ai-section">
              <h4>Zutaten</h4>
              <ul className="fridge-ai-ingredients">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i}>
                    <span className="fridge-ai-amount">{ing.amount} {ing.unit}</span>
                    <span>{ing.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {recipe.steps?.length > 0 && (
            <div className="fridge-ai-section">
              <h4>Zubereitung</h4>
              <ol className="fridge-ai-steps">
                {recipe.steps.map((step, i) => (
                  <li key={i}>{typeof step === 'string' ? step : step.text}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      <div className="fridge-ai-actions">
        <button className="fridge-btn-save" onClick={onSave} disabled={saving}>
          {saving ? FRIDGE.savingBtn : FRIDGE.saveBtn}
        </button>

        <div className="fridge-feedback-label">{FRIDGE.feedbackLabel}</div>

        <div className="fridge-ai-feedback">
          <button
            className={`fridge-feedback-btn fridge-feedback-like ${feedback === 'liked' ? 'fridge-feedback-btn--active' : ''}`}
            onClick={handleLike}
            disabled={!!feedback}
            title="Gefällt mir"
          >
            <IconFavorite size={16} />
            {feedback === 'liked' ? 'Gefällt mir!' : 'Gefällt mir'}
          </button>
          <button
            className={`fridge-feedback-btn fridge-feedback-dislike ${feedback === 'disliked' ? 'fridge-feedback-btn--active' : ''}`}
            onClick={handleDislikeOpen}
            disabled={!!feedback}
            title="Gefällt mir nicht"
          >
            <IconError size={16} />
            {feedback === 'disliked' ? 'Feedback gespeichert' : 'Gefällt mir nicht'}
          </button>
        </div>
      </div>

      {/* Dislike-Popup */}
      {showDislikePopup && (
        <div className="fridge-dislike-overlay" onClick={() => setShowDislikePopup(false)}>
          <div className="fridge-dislike-popup" onClick={e => e.stopPropagation()}>
            <div className="fridge-dislike-title">Was hat dir nicht gefallen?</div>
            <div className="fridge-dislike-tags">
              {REASON_TAGS.map(t => (
                <button
                  key={t.key}
                  className={`fridge-dislike-tag ${reasonTag === t.key ? 'fridge-dislike-tag--active' : ''}`}
                  onClick={() => setReasonTag(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <textarea
              className="fridge-dislike-note"
              placeholder="Optionale Anmerkung (z.B. keine Nüsse)…"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
            />
            <div className="fridge-dislike-actions">
              <button className="fridge-dislike-cancel" onClick={() => setShowDislikePopup(false)}>Abbrechen</button>
              <button
                className="fridge-dislike-submit"
                onClick={handleDislikeSubmit}
                disabled={!reasonTag || submitting}
              >
                {submitting ? 'Wird gespeichert…' : 'Feedback senden'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FridgeScan() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const MAX_FILES = user?.is_admin ? 99 : 5;

  const [dragOver, setDragOver] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const { result, setResult, phase, setPhase, savedMap, setSavedMap, clearScan } = useFridgeScan();
  const [scanStep, setScanStep] = useState(0);
  const [imgProgress, setImgProgress] = useState([]); // 0-100 pro Bild
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [error, setError] = useState('');
  const [quota, setQuota] = useState(null);
  const [savingIdx, setSavingIdx] = useState(null);
  const [aufwand, setAufwand] = useState('alltag');
  const [aufwandText, setAufwandText] = useState('');
  const [scanWarning, setScanWarning] = useState('');
  const [ingredientsOpen, setIngredientsOpen] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [showPushHint, setShowPushHint] = useState(false);
  // Demo-States für Gast-Ansicht
  const [demoScanned, setDemoScanned] = useState(false);
  const [demoScanning, setDemoScanning] = useState(false);

  // Scan-Ergebnis leeren wenn sich der eingeloggte User ändert
  useEffect(() => {
    clearScan();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    api.getFridgeQuota().then(setQuota).catch(() => {});
    api.getProfile().then(data => {
      if (data.user?.fridge_preference) setAufwand(data.user.fridge_preference);
      if (data.user?.fridge_preference_text) setAufwandText(data.user.fridge_preference_text);
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  function resizeImage(file, maxPx = 1920, quality = 0.85) {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const { width: w, height: h } = img;
        const scale = maxPx / Math.max(w, h);
        const canvas = document.createElement('canvas');
        canvas.width  = scale < 1 ? Math.round(w * scale) : w;
        canvas.height = scale < 1 ? Math.round(h * scale) : h;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file), 'image/jpeg', quality);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  async function addFile(file) {
    if (!file) return;
    if (selectedFiles.length >= MAX_FILES) return;
    setError('');
    const resized = await resizeImage(file);
    setSelectedFiles(prev => [...prev, resized]);
    setPreviews(prev => [...prev, URL.createObjectURL(resized)]);
  }

  function removeFile(index) {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const remaining = MAX_FILES - selectedFiles.length;
    files.slice(0, remaining).forEach(addFile);
  }

  function mapScanError(err) {
    const msg = (err.message || '').toLowerCase();
    if (err.status === 401 || msg.includes('401') || msg.includes('unauthorized'))
      return 'Deine Sitzung ist abgelaufen. Bitte lade die Seite neu und melde dich erneut an.';
    if (err.code === 'QUOTA_EXCEEDED' || msg.includes('limit') || msg.includes('quota'))
      return 'Du hast dein Wochenlimit für Kühlschrank-Scans erreicht. Nächste Woche wieder verfügbar.';
    if (msg.includes('timeout') || msg.includes('aborted') || err.name === 'AbortError')
      return 'Der Scan hat zu lange gedauert. Tipp: weniger Bilder verwenden oder Bildqualität verbessern.';
    if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network'))
      return 'Verbindungsproblem. Bitte deine Internetverbindung prüfen und erneut versuchen.';
    if (msg.includes('kein') || msg.includes('keine') || msg.includes('erkannt'))
      return err.message;
    return err.message || 'Scan fehlgeschlagen. Bitte versuche es erneut.';
  }

  async function handleScan() {
    if (noAccess) { setShowQuotaModal(true); return; }
    if (!selectedFiles.length) { setError('Bitte zuerst mindestens ein Bild auswählen.'); return; }

    setPhase('scanning');
    setError('');
    setScanWarning('');
    setScanStep(0);
    setImgProgress(selectedFiles.map(() => 0));

    // Fortschritts-Animation läuft bis echte Daten ankommen
    const progressInterval = setInterval(() => {
      setImgProgress(prev => {
        const next = [...prev];
        const activeIdx = next.findIndex(p => p < 90);
        if (activeIdx >= 0) next[activeIdx] = Math.min(90, next[activeIdx] + 2);
        return next;
      });
    }, 300);

    // Warnung dynamisch nach Bildanzahl: je 8s pro Bild + 20s Basis
    const warningDelay = (selectedFiles.length * 8 + 20) * 1000;
    const warningTimer = setTimeout(() => {
      setScanWarning(FRIDGE.scanWarning);
    }, warningDelay);

    const streamResult = { ingredients: [], db_recipes: [], ai_recipes: [] };

    try {
      await api.fridgeScanStream(selectedFiles, (event) => {
        if (event.type === 'progress') {
          setScanStep(1);
        } else if (event.type === 'ingredients') {
          clearInterval(progressInterval);
          clearTimeout(warningTimer);
          setScanWarning('');
          setImgProgress(selectedFiles.map(() => 100));
          streamResult.ingredients = event.data;
          setScanStep(2);
          setResult({ ...streamResult });
          setPhase('results');
          if (event.data?.length > 0) {
            setScanSuccess(true);
            setTimeout(() => setScanSuccess(false), 4000);
            // Push-Hint anzeigen wenn noch nicht dismissed und keine Subscription aktiv
            const dismissed = localStorage.getItem('push_hint_dismissed');
            const cooldownOk = !dismissed || (Date.now() - Number(dismissed)) > 3 * 24 * 60 * 60 * 1000;
            if (cooldownOk) {
              isPushEnabled().then(enabled => { if (!enabled) setShowPushHint(true); }).catch(() => {});
            }
          }
          api.getFridgeQuota().then(setQuota).catch(() => {});
        } else if (event.type === 'db_recipes') {
          streamResult.db_recipes = event.data;
          setResult({ ...streamResult });
        } else if (event.type === 'recipe') {
          streamResult.ai_recipes = [...streamResult.ai_recipes, event.data];
          setResult({ ...streamResult });
        } else if (event.type === 'error') {
          throw new Error(event.message);
        }
      }, 90000, aufwand, aufwandText);
    } catch (err) {
      clearInterval(progressInterval);
      clearTimeout(warningTimer);
      setScanWarning('');
      setPhase('upload');
      setError(mapScanError(err));
    }
  }

  async function handleSaveAi(recipe, idx) {
    if (!recipe) return;
    setSavingIdx(idx);
    try {
      const res = await api.saveFridgeAiRecipe(recipe);
      setSavedMap(prev => ({ ...prev, [idx]: res.recipe_id }));
    } catch (err) {
      setError(err.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSavingIdx(null);
    }
  }

  function handleReset() {
    clearScan();
    previews.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviews([]);
    setError('');
    setSavingIdx(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  const noAccess = quota && quota.pkg === 'free' && quota.trial_used;

  const FRIDGE_ITEMS = [
    { name: 'Hackfleisch', mhd: 'HEUTE!', cls: 'warn', fill: 98 },
    { name: 'Joghurt',     mhd: 'Noch 3 Tage', cls: 'warn', fill: 55 },
    { name: 'Paprika',     mhd: 'Noch 5 Tage', cls: 'warn', fill: 38 },
    { name: 'Eier',        mhd: 'Noch 12 Tage', cls: 'ok', fill: 12 },
    { name: 'Käse',        mhd: 'Noch 8 Tage', cls: 'ok', fill: 22 },
    { name: 'Karotten',    mhd: 'Noch 10 Tage', cls: 'ok', fill: 18 },
  ];
  const DEMO_RECIPES = [
    { emoji:'🍝', title:'Hackfleisch-Paprika-Pfanne', match:'Hackfleisch, Paprika, Eier', pct:'95%', time:'25 Min.' },
    { emoji:'🥘', title:'Paprika-Joghurt-Gulasch',   match:'Paprika, Joghurt, Karotten', pct:'88%', time:'40 Min.' },
    { emoji:'🍳', title:'Rührei mit Paprika & Käse',  match:'Eier, Paprika, Käse', pct:'82%', time:'15 Min.' },
  ];

  if (!user) return (
    <div className="demo-feature-page" style={{ padding: '0 16px' }}>
      <div className="demo-feature-hero">
        <div className="demo-feature-icon">🧊</div>
        <h1 className="demo-feature-title">Kühlschrank-Scan</h1>
        <p className="demo-feature-sub">
          Foto vom Kühlschrank machen — Strixly erkennt alle Zutaten, schätzt die Haltbarkeit
          und schlägt passende Rezepte vor. Nichts mehr wegschmeißen.
        </p>
      </div>

      <div className="demo-mockup">
        <div className="demo-mockup-bar">
          <div className="demo-mockup-dot" style={{background:'#ff5f57'}}/>
          <div className="demo-mockup-dot" style={{background:'#ffbd2e'}}/>
          <div className="demo-mockup-dot" style={{background:'#28c840'}}/>
          <span style={{marginLeft:8}}>Kühlschrank-Scan — Demo</span>
        </div>
        <div className="demo-mockup-body">
          {!demoScanned ? (
            <div style={{textAlign:'center',padding:'32px 0'}}>
              <div style={{
                width:120, height:120, borderRadius:16,
                background:'var(--surface, #f5f5f5)', border:'2px dashed var(--border, #e5e5e5)',
                margin:'0 auto 20px',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'3rem'
              }}>🧊</div>
              <p style={{color:'var(--txt3, #888)',fontSize:'0.88rem',marginBottom:20}}>
                Klick auf "Scan simulieren" um das Ergebnis zu sehen
              </p>
              <button
                onClick={() => { setDemoScanning(true); setTimeout(() => { setDemoScanning(false); setDemoScanned(true); }, 1800); }}
                disabled={demoScanning}
                style={{
                  background:'var(--green, #2da86a)', color:'#fff',
                  border:'none', borderRadius:10, padding:'12px 28px',
                  fontSize:'0.95rem', fontWeight:700, cursor:'pointer',
                  opacity: demoScanning ? 0.7 : 1,
                }}
              >
                {demoScanning ? '🔍 Analysiere…' : '📸 Scan simulieren'}
              </button>
            </div>
          ) : (
            <>
              <div style={{marginBottom:20}}>
                <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--txt3, #888)',marginBottom:10}}>
                  ✓ 6 ZUTATEN ERKANNT
                </div>
                <div className="demo-ing-grid">
                  {FRIDGE_ITEMS.map(i => (
                    <span key={i.name} className={`demo-ing-chip${i.cls === 'warn' ? ' warn' : ''}`}>
                      {i.cls === 'warn' ? '⚠️ ' : '✓ '}{i.name}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{marginBottom:20}}>
                <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--txt3, #888)',marginBottom:10}}>
                  ⏱ HALTBARKEITS-SCHÄTZUNG
                </div>
                {FRIDGE_ITEMS.slice(0,3).map(item => (
                  <div key={item.name} style={{
                    background:'var(--surface, #f5f5f5)', borderRadius:8,
                    padding:'10px 14px', marginBottom:8,
                  }}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                      <span style={{fontWeight:600,fontSize:'0.85rem'}}>{item.name}</span>
                      <span style={{
                        fontSize:'0.72rem',fontWeight:700,
                        color: item.fill > 80 ? '#dc2626' : '#d97706',
                        background: item.fill > 80 ? '#fee2e2' : '#fff3cd',
                        padding:'2px 8px',borderRadius:20,
                      }}>{item.mhd}</span>
                    </div>
                    <div style={{height:4,background:'var(--border, #e5e5e5)',borderRadius:2}}>
                      <div style={{
                        height:'100%', borderRadius:2,
                        width:`${item.fill}%`,
                        background: item.fill > 80 ? '#dc2626' : '#d97706',
                      }}/>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--txt3, #888)',marginBottom:10}}>
                  🍽️ PASSENDE REZEPTE FÜR DEINE ZUTATEN
                </div>
                {DEMO_RECIPES.map(r => (
                  <div key={r.title} className="demo-recipe-result">
                    <div className="demo-recipe-result-emoji">{r.emoji}</div>
                    <div className="demo-recipe-result-body">
                      <div className="demo-recipe-result-title">{r.title}</div>
                      <div className="demo-recipe-result-meta">⏱ {r.time}</div>
                      <div className="demo-recipe-result-match">✓ {r.match}</div>
                    </div>
                    <div style={{fontSize:'0.8rem',fontWeight:800,color:'var(--green, #2da86a)',flexShrink:0}}>{r.pct}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="demo-cta-box">
        <h3>Deinen Kühlschrank scannen</h3>
        <p>Nie wieder ratlos vor dem Kühlschrank stehen.<br/>Strixly findet das Rezept für das was du hast — in Sekunden.</p>
        <a href="/login?tab=register" className="demo-cta-btn">Kostenlos ausprobieren →</a>
        <div className="demo-cta-microcopy">Keine Kreditkarte · DSGVO-konform · Server in Österreich/EU</div>
      </div>
    </div>
  );

  return (
    <div className="fridge-page">

      {showQuotaModal && (
        <div className="fridge-quota-overlay" onClick={() => setShowQuotaModal(false)}>
          <div className="fridge-quota-modal" onClick={e => e.stopPropagation()}>
            <div className="fridge-quota-modal-icon">🦉</div>
            <h3 className="fridge-quota-modal-title">
              Du bist ein echter Lebensmittel-Retter!
            </h3>
            <p className="fridge-quota-modal-text">
              Deine kostenlosen Scans sind aufgebraucht. Mit Strixly Pro scannst du
              unbegrenzt weiter — und rettest noch mehr Essen vor dem Müll.
            </p>
            <Link to="/preise" className="fridge-quota-modal-btn">
              Strixly Pro freischalten →
            </Link>
            <button
              className="fridge-quota-modal-later"
              onClick={() => setShowQuotaModal(false)}
            >
              Später vielleicht
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">{FRIDGE.pageTitle}</div>
          <div className="page-sub">{FRIDGE.pageSub}</div>
        </div>
      </div>


      {error && <div className="fridge-error">{error}</div>}
      {scanWarning && phase === 'scanning' && (
        <div className="fridge-warning">{scanWarning}</div>
      )}

      {/* ── Phase: Upload ── */}
      {phase === 'upload' && (
        <div className="fridge-upload-area">

          {/* Thumbnails der bereits hinzugefügten Bilder */}
          {previews.length > 0 && (
            <div className="fridge-thumb-strip">
              {previews.map((src, i) => (
                <div key={i} className="fridge-thumb">
                  <img src={src} alt={`Bild ${i + 1}`} className="fridge-thumb-img" />
                  <button className="fridge-thumb-remove" onClick={() => removeFile(i)}><IconClose size={12} /></button>
                </div>
              ))}
            </div>
          )}

          {/* Hinzufügen-Bereich */}
          {selectedFiles.length < MAX_FILES && (
            <div className="fridge-add-area">
              <button type="button" className="fridge-camera-primary" onClick={() => setShowCamera(true)}>
                <IconNavCamera className="fridge-camera-primary-icon" size={26} />
                <span className="fridge-camera-primary-text">
                  <strong>Foto aufnehmen</strong>
                  <small>Kamera direkt öffnen</small>
                </span>
              </button>
              <div
                className={`fridge-drop-zone ${dragOver ? 'fridge-drop-zone--over' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <label className="fridge-drop-content" htmlFor="fridge-gallery-input">
                  <div className="fridge-drop-icon">
                    {previews.length > 0 ? <IconNavPlus size={28} /> : <IconPhoto size={28} />}
                  </div>
                  <div className="fridge-drop-title">
                    {previews.length > 0 ? 'Weiteres Foto aus Galerie' : 'Aus Galerie wählen'}
                  </div>
                  <div className="fridge-drop-hint">oder Bild hier reinziehen</div>
                  <div className="fridge-drop-formats">JPG · PNG · WebP</div>
                </label>
              </div>
            </div>
          )}

          {selectedFiles.length >= MAX_FILES && MAX_FILES < 99 && (
            <div className="fridge-max-hint"><IconCheck size={14} /> Maximum von {MAX_FILES} Fotos erreicht</div>
          )}

          <input
            ref={fileRef}
            id="fridge-gallery-input"
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              const remaining = MAX_FILES - selectedFiles.length;
              Array.from(e.target.files).slice(0, remaining).forEach(addFile);
              e.target.value = '';
            }}
          />

          {showCamera && (
            <CameraCapture
              onCapture={file => addFile(file)}
              onClose={() => setShowCamera(false)}
              maxPhotos={MAX_FILES}
              currentCount={selectedFiles.length}
            />
          )}

          {/* Aufwand-Auswahl */}
          <div className="fridge-aufwand">
            <div className="fridge-aufwand-label">Kochaufwand</div>
            <div className="fridge-aufwand-chips">
              {[
                { key: 'schnell',    label: 'Schnell', hint: '≤ 30 Min.' },
                { key: 'alltag',     label: 'Alltag',  hint: 'Hausmannskost' },
                { key: 'aufwendig',  label: 'Aufwendig', hint: 'Mehr Zeit' },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  className={`fridge-aufwand-chip${aufwand === opt.key ? ' fridge-aufwand-chip--active' : ''}`}
                  onClick={() => setAufwand(opt.key)}
                >
                  {opt.label}
                  <span className="fridge-aufwand-hint">{opt.hint}</span>
                </button>
              ))}
            </div>
            <textarea
              className="fridge-aufwand-text"
              placeholder="Eigene Wünsche (optional): z.B. vegetarisch, für Kinder, wenig Kohlenhydrate, mediterran, ohne Zwiebeln"
              value={aufwandText}
              onChange={e => setAufwandText(e.target.value)}
              rows={2}
            />
          </div>

          {selectedFiles.length > 0 && (
            <button className="fridge-btn-scan" onClick={handleScan}>
              <IconNavSearch size={16} /> {selectedFiles.length === 1 ? '1 Foto' : `${selectedFiles.length} Fotos`} scannen
            </button>
          )}
        </div>
      )}


      {/* ── Phase: Scanning ── */}
      {phase === 'scanning' && (
        <div className="fridge-scanning">
          <div className="fridge-scan-layout">
            <div className="fridge-scan-images">
              {previews.map((src, i) => {
                const pct = imgProgress[i] ?? 0;
                const done = pct >= 100;
                const active = !done && (imgProgress.findIndex(p => p < 95) === i || pct > 0);
                return (
                  <div key={i} className="fridge-scan-image-wrap">
                    <img src={src} alt={`Scan ${i + 1}`} className="fridge-scan-img" />
                    <div className={`fridge-scan-overlay${done ? ' fridge-scan-overlay--done' : active ? ' fridge-scan-overlay--active' : ''}`}>
                      <span className="fridge-scan-pct">
                        {done ? <IconCheck size={14} /> : `${pct}%`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="fridge-scan-steps">
              {SCAN_STEPS.map((step, i) => (
                <div key={i} className={`fridge-step ${i < scanStep ? 'fridge-step--done' : i === scanStep ? 'fridge-step--active' : 'fridge-step--waiting'}`}>
                  <span className="fridge-step-dot">
                    {i < scanStep ? <IconCheck size={12} /> : i === scanStep ? '●' : '○'}
                  </span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Phase: Ergebnisse ── */}
      {phase === 'results' && result && (
        <div className="fridge-results">

          {scanSuccess && (
            <div className="scan-success-banner">
              <span className="scan-success-icon"><IconCheck size={16} /></span>
              <span>
                <strong>{result.ingredients.length} Zutaten erkannt</strong>
                {' '}— ich suche passende Rezepte für dich.
              </span>
            </div>
          )}

          {/* ── Push-Hint Banner ── */}
          {showPushHint && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '0.75rem', padding: '0.75rem 1rem', marginBottom: '1rem',
              background: 'var(--green-pale)', borderRadius: 10, border: '1px solid var(--border)',
            }}>
              <span style={{ fontSize: '0.9rem', color: 'var(--txt)' }}>
                🔔 {PUSH.hintText}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem' }}
                  onClick={async () => {
                    try {
                      await registerPush();
                      setShowPushHint(false);
                      localStorage.removeItem('push_hint_dismissed');
                    } catch { /* User hat abgelehnt — Banner schließen */ setShowPushHint(false); }
                  }}
                >
                  {PUSH.btnEnable}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem' }}
                  onClick={() => {
                    setShowPushHint(false);
                    localStorage.setItem('push_hint_dismissed', String(Date.now()));
                  }}
                  aria-label="Schließen"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {result.ingredients?.length === 0 && (
            <div className="fridge-warning">
              {FRIDGE.emptyIngredients}
            </div>
          )}

          {/* Erkannte Zutaten – klappbar */}
          {result.ingredients?.length > 0 && (
            <div className="fridge-section">
              <button
                className="fridge-ing-toggle"
                onClick={() => setIngredientsOpen(o => !o)}
              >
                <span>🥦 Erkannte Zutaten</span>
                <span className="fridge-ing-toggle-right">
                  <span className="fridge-section-count">{result.ingredients.length}</span>
                  <span className="fridge-ing-toggle-arrow">{ingredientsOpen ? '▲' : '▼'}</span>
                </span>
              </button>
              {ingredientsOpen && (
                <div className="fridge-ing-body">
                  <div className="fridge-ing-list">
                    {result.ingredients.map((ing, i) => (
                      <ConfidenceChip key={i} name={ing.name} confidence={ing.confidence} />
                    ))}
                  </div>
                  <div className="fridge-ing-legend">
                    <span className="fridge-ing-chip fridge-ing-chip--high">Sicher</span>
                    <span className="fridge-ing-chip fridge-ing-chip--mid">Wahrscheinlich</span>
                    <span className="fridge-ing-chip fridge-ing-chip--low">Unsicher</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 2-Spalten-Grid: MHD | KI-Rezepte */}
          {(() => {
            const warnings = (result.ingredients || [])
              .filter(i => i.haltbarkeit_tage != null && i.haltbarkeit_tage <= 5)
              .sort((a, b) => a.haltbarkeit_tage - b.haltbarkeit_tage);
            const hasMhd = warnings.length > 0;
            return (
              <div className={hasMhd ? 'fridge-results-grid' : ''}>
                {hasMhd && (
                  <div className="fridge-section fridge-freshness-section">
                    <h2 className="fridge-section-title">
                      ⏱ Bitte bald verwerten
                      <span className="fridge-section-count">{warnings.length}</span>
                    </h2>
                    <div className="fridge-prio-list">
                      {warnings.map((ing, i) => {
                        const days = ing.haltbarkeit_tage;
                        const isToday = days <= 0;
                        const isCritical = days <= 2;
                        const barPct = Math.min(100, Math.round((1 - days / 5) * 100));
                        const pillClass = isToday ? 'pill pill-solid prio-pill-today' : isCritical ? 'pill pill-red' : 'pill pill-amber';
                        const pillText = isToday ? 'HEUTE!' : `NOCH ${days} T.`;
                        const barClass = isCritical ? 'pf-red' : 'pf-amber';
                        return (
                          <div key={i} className="fridge-prio-card">
                            <div className="prio-normal">
                              <div className="prio-top">
                                <span className="prio-name">{ing.name}</span>
                                <span className={pillClass} style={{ fontSize: '0.62rem' }}>{pillText}</span>
                              </div>
                              <div className="prio-sub">Schätzwert ab Scan-Zeitpunkt</div>
                              <div className="prio-bar">
                                <div className={`prio-fill ${barClass}`} style={{ width: `${barPct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="fridge-freshness-hint">
                      Schätzwerte — kein MHD vom Etikett · Vollständiges Haltbarkeits-Tracking kommt bald ✨
                    </p>
                  </div>
                )}

                <div className="fridge-section">
                  <h2 className="fridge-section-title">
                    <IconChefHat size={18} /> {result.ai_recipes?.length > 0
                      ? (result.ai_recipes.length === 1 ? 'Dein persönliches KI-Rezept' : `${result.ai_recipes.length} KI-Rezeptvorschläge`)
                      : 'KI-Rezepte werden generiert…'}
                  </h2>
                  {result.ai_recipes?.length > 0 ? (
                    <div className="fridge-ai-list">
                      {result.ai_recipes.map((recipe, idx) => (
                        recipe?.title ? (
                          <div key={idx}>
                            {savedMap[idx] !== undefined ? (
                              <div className="fridge-saved-card">
                                <span><IconCheck size={14} /> Gespeichert</span>
                                <Link to={`/edit/${savedMap[idx]}`} className="fridge-saved-link">Rezept öffnen →</Link>
                              </div>
                            ) : (
                              <AiRecipeCard
                                recipe={recipe}
                                onSave={() => handleSaveAi(recipe, idx)}
                                saving={savingIdx === idx}
                              />
                            )}
                          </div>
                        ) : null
                      ))}
                    </div>
                  ) : (
                    <div className="fridge-stream-loading">
                      <span className="fridge-stream-spinner"></span>
                      {FRIDGE.streamLoading}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* DB-Rezepte – volle Breite */}
          {result.db_recipes == null ? (
            <div className="fridge-section">
              <div className="fridge-stream-loading">
                <span className="fridge-stream-spinner"></span>
                Passende Rezepte werden gesucht…
              </div>
            </div>
          ) : result.db_recipes.length > 0 ? (
            <div className="fridge-section">
              <h2 className="fridge-section-title">
                <IconNavShopping size={18} /> Passende Rezepte aus der Datenbank
                <span className="fridge-section-count">{result.db_recipes.length}</span>
              </h2>
              <div className="fridge-db-grid">
                {result.db_recipes.map(r => <DbRecipeCard key={r.id} recipe={r} />)}
              </div>
            </div>
          ) : (
            <div className="fridge-section">
              <p className="fridge-no-match">Keine passenden Rezepte in der Datenbank gefunden — aber das KI-Rezept oben ist frisch für dich generiert!</p>
            </div>
          )}

          <button className="fridge-btn-reset" onClick={handleReset}>
            {FRIDGE.resetBtn}
          </button>
        </div>
      )}
    </div>
  );
}
